import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMovie, paypalCaptureOrder, paypalCheckout, stripeCheckout } from '../services/api';
import { Movie } from '../types/api';

declare global {
  interface Window {
    paypal?: any;
  }
}

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
const PAYPAL_SDK_URL = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=AUD&intent=capture&components=buttons&disable-funding=card,credit`;

const loadPayPalSdk = async () => {
  if (window.paypal) return;
  if (document.querySelector('script[src^="https://www.paypal.com/sdk/js"]')) return;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PAYPAL_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
    document.body.appendChild(script);
  });
};

export const MovieDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [error, setError] = useState('');
  const [renting, setRenting] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const paypalButtonsRef = useRef<any | null>(null);
  const paypalRenderedRef = useRef(false);
  const paypalPaymentIdRef = useRef<number | null>(null);

  useEffect(() => {
    getMovie(id).then((response) => setMovie(response.data)).catch(() => setError('Movie not found'));
  }, [id]);

  useEffect(() => {
    if (!movie || searchParams.get('payment') !== '1') return;
    if (isAuthenticated) {
      setAuthDialogOpen(false);
      setPaymentDialogOpen(true);
    } else {
      setPaymentDialogOpen(false);
      setAuthDialogOpen(true);
    }
  }, [isAuthenticated, movie, searchParams]);

  useEffect(() => {
    if (!paymentDialogOpen || !movie) return;
    let cancelled = false;
    let renderedContainer: HTMLDivElement | null = null;

    const renderButtons = async () => {
      if (!PAYPAL_CLIENT_ID) {
        setError('PayPal is not configured yet.');
        return;
      }
      if (paypalRenderedRef.current) return;
      try {
        await loadPayPalSdk();
        for (let i = 0; i < 20 && !window.paypal; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        if (cancelled || !window.paypal || !paypalContainerRef.current) return;

        renderedContainer = paypalContainerRef.current;
        renderedContainer.innerHTML = '';
        const buttons = window.paypal.Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
          createOrder: async () => {
            setError('');
            const response = await paypalCheckout(movie.id);
            paypalPaymentIdRef.current = response.paymentId;
            return response.paypalOrderId;
          },
          onApprove: async (data: any) => {
            setRenting(true);
            try {
              const capture = await paypalCaptureOrder(data.orderID);
              if (capture.status === 'COMPLETED' && paypalPaymentIdRef.current) {
                navigate(`/payment/success?provider=paypal&movieId=${movie.id}&paymentId=${paypalPaymentIdRef.current}`);
                return;
              }
              setError('PayPal payment was not completed. Your rental has not been activated.');
            } catch {
              setError('Could not confirm the PayPal payment. Please try again.');
            } finally {
              setRenting(false);
            }
          },
          onCancel: () => setError('Payment was cancelled. Your rental has not been activated.'),
          onError: () => setError('PayPal payment failed. Please try again or use Stripe.'),
        });

        if (typeof buttons.isEligible === 'function' && !buttons.isEligible()) {
          setError('PayPal is not available right now.');
          return;
        }

        paypalButtonsRef.current = buttons;
        await buttons.render(renderedContainer);
        if (!cancelled) paypalRenderedRef.current = true;
      } catch {
        setError('Unable to load PayPal at this time.');
      }
    };

    renderButtons();

    return () => {
      cancelled = true;
      if (paypalButtonsRef.current && typeof paypalButtonsRef.current.close === 'function') {
        try {
          paypalButtonsRef.current.close();
        } catch {
          // Ignore PayPal SDK cleanup failures.
        }
      }
      paypalButtonsRef.current = null;
      paypalRenderedRef.current = false;
      if (renderedContainer) {
        renderedContainer.innerHTML = '';
      }
    };
  }, [movie, navigate, paymentDialogOpen]);

  const openRentFlow = () => {
    setError('');
    if (isAuthenticated) {
      setAuthDialogOpen(false);
      setPaymentDialogOpen(true);
    } else {
      setPaymentDialogOpen(false);
      setAuthDialogOpen(true);
    }
  };

  const rent = async () => {
    if (!movie) return;
    setRenting(true);
    setError('');
    try {
      const checkout = await stripeCheckout(movie.id);
      window.location.href = checkout.url;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setPaymentDialogOpen(false);
        setAuthDialogOpen(true);
        return;
      }
      setError('Please login before renting this movie.');
    } finally {
      setRenting(false);
    }
  };

  if (error && !movie) {
    return <main className="mx-auto max-w-4xl px-4 py-10 text-red-700">{error}</main>;
  }

  if (!movie) {
    return <main className="mx-auto max-w-4xl px-4 py-10 text-stone-300">Loading movie...</main>;
  }

  const returnToPayment = `/movies/${movie.id}?payment=1`;
  const encodedReturnToPayment = encodeURIComponent(returnToPayment);

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
        <section>
          <div className="overflow-hidden rounded-lg border border-stone-800 bg-stone-900">
            <img src={movie.thumbnailUrl} alt={movie.title} className="aspect-video w-full object-cover" />
          </div>
          <h1 className="mt-6 text-4xl font-bold text-white">{movie.title}</h1>
          <p className="mt-4 text-lg leading-8 text-stone-300">{movie.description}</p>
          {movie.trailerUrl && (
            <a className="mt-4 inline-block font-semibold text-white underline decoration-[#ffd460] decoration-2 underline-offset-4" href={movie.trailerUrl} target="_blank" rel="noreferrer">
              Watch trailer
            </a>
          )}
        </section>
        <aside className="h-fit rounded-lg border border-stone-800 bg-stone-800 p-6 shadow-sm">
          <div className="text-sm font-medium text-stone-300">Rental price</div>
          <div className="mt-1 text-4xl font-bold text-white">${Number(movie.price).toFixed(2)}</div>
          <p className="mt-3 text-sm text-stone-300">Unlimited playback for 48 hours from first play.</p>
          {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <button className="mt-6 w-full rounded-md bg-[#ffd460] px-4 py-3 font-semibold text-stone-950 hover:bg-[#ffc83d]" onClick={openRentFlow}>
            Rent Now
          </button>
          <Link className="mt-4 block text-center text-sm font-semibold text-stone-300 hover:text-white" to="/">
            Back to catalog
          </Link>
        </aside>
      </div>

      {paymentDialogOpen && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/70 px-4 py-6">
          <div className="w-full max-w-md rounded-lg border border-stone-500 bg-stone-800 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">Rent {movie.title}</h2>
                <div className="mt-4 rounded-lg border border-stone-800 bg-stone-900 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-stone-300">Rental price</div>
                  <div className="mt-1 text-3xl font-bold text-white">${Number(movie.price).toFixed(2)}</div>
                  <p className="mt-1 text-sm text-stone-300">48 hours from first play</p>
                </div>
              </div>
              <button className="rounded-md px-2 py-1 text-xl leading-none text-stone-400 hover:bg-stone-800 hover:text-white" onClick={() => setPaymentDialogOpen(false)} aria-label="Close payment options">
                x
              </button>
            </div>

            {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            <div className="mt-6 space-y-3">
              <div ref={paypalContainerRef} className="min-h-12" />
              {renting && <p className="text-center text-sm text-stone-300">Confirming PayPal payment...</p>}
              <button
                className="flex w-full items-center justify-center rounded-md bg-[#675dff] px-4 py-3 text-base font-semibold text-white hover:bg-[#534bff] focus:outline-none focus:ring-2 focus:ring-[#675dff] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={rent}
                disabled={renting}
              >
                {renting ? (
                  'Processing...'
                ) : (
                  <>
                    Pay with
                    <img src="/images/stripe_logo.png" alt="Stripe" className="ml-1.5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {authDialogOpen && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/70 px-4 py-6">
          <div className="w-full max-w-md rounded-lg border border-stone-800 bg-stone-950 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">Login to rent</h2>
                <p className="mt-2 text-sm text-stone-300">Please login or sign up before choosing a payment method.</p>
              </div>
              <button className="rounded-md px-2 py-1 text-xl leading-none text-stone-400 hover:bg-stone-800 hover:text-white" onClick={() => setAuthDialogOpen(false)} aria-label="Close login options">
                x
              </button>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Link className="rounded-md bg-[#ffd460] px-4 py-3 text-center font-semibold text-stone-950 hover:bg-[#ffc83d]" to={`/login?returnTo=${encodedReturnToPayment}`}>
                Login
              </Link>
              <Link className="rounded-md border border-stone-700 bg-stone-900 px-4 py-3 text-center font-semibold text-white hover:border-[#ffd460] hover:bg-stone-800" to={`/signup?returnTo=${encodedReturnToPayment}`}>
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};
